import { Source } from '../../../globals/enums';
import type {
  TechLogToRusadaDefectInput,
  TechLogToRusadaDefectCloseInput,
  TechLogToRusadaDefectDeferInput,
} from './validation';

/**
 * Path-specific mock data for TechLog → Rusada defect transformation
 * This object matches the exact shape required by the inputZod schema and path-specific context type
 * Uses realistic aviation maintenance defect data with proper ATA chapters and defect details
 */
export const mockData: TechLogToRusadaDefectInput = {
  techlog: {
    defect: {
      id: 'TLD-2024-001456',
      aircraftId: 'TL-AC003',
      sectorId: 'TLS-2024-001234',
      reportAirportId: 1001,
      resolutionAirportId: 1002,
      details:
        'Left engine oil pressure warning light illuminated during climb. Engine parameters within normal limits. Oil quantity normal.',
      itemDescription: 'Engine Oil Pressure Warning System',
      number: 2024001456,
      rectificationIntervalExtension: {
        id: 'RIE-2024-001',
        defectId: 'TLD-2024-001456',
        extensionDate: '2024-01-20',
        extensionDateDue: '2024-02-15',
        createdAt: '2024-01-20T12:00:00Z',
        updatedAt: '2024-01-20T12:00:00Z',
      },
      dueDate: '2024-02-15',
      dueDateTime: '2024-02-15T14:00:00Z',
      estimatedDate: '2024-02-10',
      raisedAt: '2024-01-20T11:30:00Z',
      dueFlightSeconds: 36000,
      dueApuSeconds: 1800,
      dueCycles: 50,
      dueLandings: 25,
      status: 'OPEN',
      reference: 'DEF-BA-2024-001456',
      ataChapter: 79,
      ataSection: 21,
      flightsDue: 15,
      limitations:
        'No ETOPS operations permitted. Dispatch with restrictions per MEL 79-21-01.',
      melItemId: 'MEL-79-21-01',
      melRectificationId: 'RECT-79-21-001',
      placardProcedureCompleted: true,
      operationalProcedureCompleted: true,
      deferred: true,
      raisedFromId: 'TL-USER-PILOT-001',
      reportedById: 'TL-USER-PILOT-001',
      source: Source.TECHLOG,
      importedAt: '2024-01-20T11:30:00Z',
      defectType: 'MAINTENANCE',
      rectificationCategory: 'LINE_MAINTENANCE',
      tripEntryId: 'TLT-2024-5678',
      serviceabilityLimiting: true,
      limitFlightSeconds: 36000,
      limitSeconds: 86400,
      limitFlightDays: 3,
      limitCycles: 50,
      limitFlights: 15,
      limitCalendarDays: 10,
      limitApuSeconds: 1800,
      limitOther: 'No night operations in Class A airspace',
      deferApprovalReference: 'DA-79-21-2024-001',
      reasonToDefer:
        'Engine oil pressure sensor intermittent fault. Replacement sensor not available at departure station.',
      editedById: 'TL-USER-MAINT-001',
      editedDate: '2024-01-20T12:00:00Z',
      eropsPrevented: true,
      placardNumber: 7921001,
      campSyncDate: '2024-01-20T12:15:00Z',
      campReference: 'CAMP-DEF-001456',
      deferralSignatureId: 'TL-SIG-DEF-001',
      deferredAt: '2024-01-20T12:00:00Z',
      deferralLicenceNumber: 'AME-UK-001234',
      deferralApprovalNumber: 'DA-2024-001456',
      deferredById: 'TL-USER-AME-001',
      intermittentFaultId: 'IF-79-21-001',
      reportedByType: 'FLIGHT_CREW',
      createdAt: '2024-01-20T11:30:00Z',
      updatedAt: '2024-01-20T12:15:00Z',
      deferUsing: 'MEL',
      thirdPartyReferences: {
        camp: 'CAMP-DEF-001456',
        amos: 'AMOS-WO-789123',
        rusada: 'RUS-DEF-456789',
      },
      troubleshootingSteps:
        '1. Checked oil quantity - normal\n2. Performed engine oil pressure test - within limits\n3. Inspected oil pressure sensor wiring - no damage visible\n4. Reviewed engine trend data - no abnormal patterns\n5. Deferred per MEL 79-21-01',
    },
    reportingUser: {
      id: 'TL-USER-PILOT-001',
      operatorId: 'OP-BA-001',
      firstName: 'John',
      lastName: 'Pilot',
      email: 'john.pilot@airline.com',
      position: 'Captain',
      employeeNumber: 'EMP-12345',
      amosUserSign: 'JP',
      status: 'active',
      hasLogin: true,
      createdAt: '2023-01-01T00:00:00Z',
      updatedAt: '2024-01-20T11:30:00Z',
    },
  },
  rusada: {
    registration: {
      id: 42,
      assetId: 187,
      registration: 'G-ZBKA',
      statusId: 1,
      status: 'ACTIVE',
      serialNo: '60276',
      operator: 'British Airways',
      isLocked: false,
    },
    defect: {
      id: 456789,
      techLogId: 54321,
      flightId: 97081,
    },
    defectCategories: [
      {
        id: 1,
        description: 'Engine related defects and maintenance items',
        category: 'Engine',
        isComponentDefect: true,
      },
      {
        id: 2,
        description: 'Engine and APU oil system components',
        category: 'Oil System',
        isComponentDefect: true,
      },
      {
        id: 3,
        description: 'Electronic sensors and monitoring equipment',
        category: 'Sensors',
        isComponentDefect: false,
      },
    ],
    reportingUser: {
      id: 301,
      firstName: 'John',
      surname: 'Pilot',
      employeeUsername: 'john.pilot@airline.com',
    },
    sector: {
      techLogId: 54321,
      sectorId: 97081,
    },
  },
};

/**
 * Mock data for a resolved (closed) defect scenario
 * Used for testing CLOSE action transformations
 */
export const mockDataResolved: TechLogToRusadaDefectCloseInput = {
  techlog: {
    defect: {
      ...mockData.techlog.defect,
      status: 'resolved',
      updatedAt: '2024-01-25T14:30:00Z',
      troubleshootingSteps:
        '1. Replaced oil pressure sensor\n2. Performed ground run test - oil pressure normal\n3. Performed test flight - no further warnings\n4. Defect cleared and aircraft released to service',
    },
  },
  rusada: {
    registration: mockData.rusada.registration,
    defect: {
      ...mockData.rusada.defect!,
      flightId: 97082,
    },
    reportingUser: mockData.rusada.reportingUser!,
  },
};

/**
 * Mock data for a deferred defect scenario
 * Used for testing DEFER action transformations
 * Represents a defect that has been deferred in TechLog and needs to be deferred in Rusada
 */
export const mockDataDeferred: TechLogToRusadaDefectDeferInput = {
  techlog: {
    defect: {
      ...mockData.techlog.defect,
      deferred: true,
      status: 'DEFERRED',
      rectificationCategory: 'C',
      reasonToDefer:
        'Oil pressure sensor requires replacement. Replacement part on order, expected delivery in 5 days.',
      deferApprovalReference: 'ADD-2024-001',
      dueDate: '2024-01-30',
      dueDateTime: '2024-01-30T23:59:59Z',
      limitCalendarDays: 10,
      limitFlightSeconds: 36000,
      limitCycles: 50,
      melItemId: 'MEL-79-21-01',
      deferredAt: '2024-01-20T12:00:00Z',
      deferralLicenceNumber: 'AME-UK-001234',
      deferralApprovalNumber: 'DA-2024-001456',
      deferredById: 'TL-USER-AME-001',
      updatedAt: '2024-01-20T12:00:00Z',
    },
    deferringUser: {
      id: 'TL-USER-AME-001',
      operatorId: 'OP-BA-001',
      firstName: 'Jane',
      lastName: 'Deferrer',
      email: 'jane.deferrer@airline.com',
      position: 'AME',
      employeeNumber: 'EMP-67890',
      amosUserSign: 'JD',
      status: 'active',
      hasLogin: true,
      createdAt: '2023-01-01T00:00:00Z',
      updatedAt: '2024-01-20T12:00:00Z',
    },
  },
  rusada: {
    registration: mockData.rusada.registration,
    defect: {
      ...mockData.rusada.defect!,
      defectStatusId: 4,
      defectStatus: 'Open',
    },
    deferringUser: {
      id: 302,
      firstName: 'Jane',
      surname: 'Deferrer',
      employeeUsername: 'jane.deferrer@airline.com',
    },
    sector: mockData.rusada.sector!,
  },
};
